import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { pageColors } from './HomePage';
import apiFetch from '../api/apiFetch';

const MIN_MONTH = dayjs('2024-01-01').startOf('month');
const MAX_MONTH = dayjs('2028-12-31').startOf('month');
const CURRENT_YEAR = dayjs().year();

const PURCHASE_ORDERS_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.purchaseOrders,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(0,105,92,0.16), transparent 55%), #f5f8f7';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const MONTH_FIELD_SX = {
  width: { xs: '100%', sm: 180 },
  flex: { sm: '0 0 auto' },
};

const ACTION_BUTTON_STACK_SX = {
  width: { xs: '100%', sm: 'auto' },
  flex: { sm: '0 0 auto' },
};

const ACTION_BUTTON_SX = {
  height: 40,
  minHeight: 40,
  minWidth: { sm: 136 },
};

const TABLE_HEADER_SX = {
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
  fontWeight: 700,
};

const TABLE_ROW_HOVER_SX = {
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
};

const OVERDUE_TABLE_ROW_SX = {
  backgroundColor: 'rgba(46,125,50,0.08)',
  '&:hover': {
    backgroundColor: 'rgba(46,125,50,0.14)',
  },
};

const EXPAND_BUTTON_SX = {
  border: '1px solid',
  borderColor: 'primary.main',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(0,105,92,0.08)',
  },
};

const EXPAND_BUTTON_ACTIVE_SX = {
  ...EXPAND_BUTTON_SX,
  backgroundColor: 'rgba(0,105,92,0.12)',
};

const STATUS_PILL_SX = {
  default: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 72,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#616161',
    backgroundColor: 'rgba(97,97,97,0.10)',
    border: '1px solid rgba(97,97,97,0.16)',
  },
  success: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 72,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#2e7d32',
    backgroundColor: 'rgba(46,125,50,0.12)',
    border: '1px solid rgba(46,125,50,0.18)',
  },
  info: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 72,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#1565c0',
    backgroundColor: 'rgba(21,101,192,0.12)',
    border: '1px solid rgba(21,101,192,0.18)',
  },
  warning: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 72,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#ef6c00',
    backgroundColor: 'rgba(245,124,0,0.14)',
    border: '1px solid rgba(239,108,0,0.18)',
  },
};

const STATUS_TONE_MAP = {
  RECEIVED: 'success',
  SENT: 'info',
  CREATED: 'warning',
  DRAFT: 'default',
};

const SORT_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

function clampMonth(value) {
  if (!value || !value.isValid()) {
    return MIN_MONTH;
  }

  const normalizedValue = value.startOf('month');

  if (normalizedValue.isBefore(MIN_MONTH)) {
    return MIN_MONTH;
  }

  if (normalizedValue.isAfter(MAX_MONTH)) {
    return MAX_MONTH;
  }

  return normalizedValue;
}

function getMonthInputValue(value) {
  if (!value || !value.isValid()) {
    return '';
  }

  return value.format('YYYY-MM');
}

function normalizeText(value) {
  return String(value ?? '').trim();
}

function getStringSortValue(value) {
  return normalizeText(value).toLowerCase();
}

function getDateSortValue(value) {
  if (!value) {
    return null;
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.valueOf() : null;
}

function getDateDisplay(value) {
  const sortValue = getDateSortValue(value);
  return sortValue === null ? '-' : dayjs(sortValue).format('YYYY-MM-DD');
}

function getNumberValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function formatNumberDisplay(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return Number.isInteger(numericValue)
    ? numericValue.toLocaleString()
    : numericValue.toFixed(2);
}

function toExportValue(value) {
  const numericValue = getNumberValue(value);
  if (numericValue !== null) {
    return numericValue;
  }

  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
}

function getPurchaseOrderGroupingKey(row, index) {
  const purchaseOrderId = normalizeText(row.purchaseOrderId);
  if (purchaseOrderId) {
    return purchaseOrderId;
  }

  const customerNumber = normalizeText(row.customerNumber);
  if (customerNumber) {
    return customerNumber;
  }

  const fallbackFields = [
    normalizeText(row.supplierName),
    normalizeText(row.orderDate),
    normalizeText(row.deliveryDate),
    normalizeText(row.purchaseOrderState),
  ];

  if (fallbackFields.some(Boolean)) {
    return `po-${fallbackFields.join('|')}`;
  }

  return `unknown-${index}`;
}

function createPurchaseOrderEntry(groupKey, row) {
  const purchaseOrderId = normalizeText(row.purchaseOrderId);
  const supplierName = normalizeText(row.supplierName);
  const statusDisplay = normalizeText(row.purchaseOrderState) || 'UNKNOWN';
  const orderDateSort = getDateSortValue(row.orderDate);
  const deliveryDateSort = getDateSortValue(row.deliveryDate);
  const orderValueSort = getNumberValue(row.orderValueInSEK);

  return {
    key: groupKey,
    purchaseOrderIdDisplay: purchaseOrderId || '-',
    purchaseOrderIdSort: getStringSortValue(purchaseOrderId || groupKey),
    supplierNameDisplay: supplierName || '-',
    supplierNameSort: getStringSortValue(supplierName),
    orderDateDisplay: getDateDisplay(row.orderDate),
    orderDateSort,
    deliveryDateDisplay: getDateDisplay(row.deliveryDate),
    deliveryDateSort,
    statusDisplay,
    statusSort: getStringSortValue(statusDisplay),
    statusTone: STATUS_TONE_MAP[statusDisplay.toUpperCase()] || 'default',
    orderValueDisplay: formatNumberDisplay(row.orderValueInSEK),
    orderValueSort,
    orderValueExport: orderValueSort ?? '',
    rows: [],
  };
}

function mergePurchaseOrderEntry(entry, row) {
  const purchaseOrderId = normalizeText(row.purchaseOrderId);
  if (entry.purchaseOrderIdDisplay === '-' && purchaseOrderId) {
    entry.purchaseOrderIdDisplay = purchaseOrderId;
    entry.purchaseOrderIdSort = getStringSortValue(purchaseOrderId);
  }

  const supplierName = normalizeText(row.supplierName);
  if (entry.supplierNameDisplay === '-' && supplierName) {
    entry.supplierNameDisplay = supplierName;
    entry.supplierNameSort = getStringSortValue(supplierName);
  }

  const orderDateSort = getDateSortValue(row.orderDate);
  if (entry.orderDateSort === null && orderDateSort !== null) {
    entry.orderDateSort = orderDateSort;
    entry.orderDateDisplay = dayjs(orderDateSort).format('YYYY-MM-DD');
  }

  const deliveryDateSort = getDateSortValue(row.deliveryDate);
  if (entry.deliveryDateSort === null && deliveryDateSort !== null) {
    entry.deliveryDateSort = deliveryDateSort;
    entry.deliveryDateDisplay = dayjs(deliveryDateSort).format('YYYY-MM-DD');
  }

  if (entry.statusDisplay === 'UNKNOWN') {
    const statusDisplay = normalizeText(row.purchaseOrderState);
    if (statusDisplay) {
      entry.statusDisplay = statusDisplay;
      entry.statusSort = getStringSortValue(statusDisplay);
      entry.statusTone = STATUS_TONE_MAP[statusDisplay.toUpperCase()] || 'default';
    }
  }

  const orderValueSort = getNumberValue(row.orderValueInSEK);
  if (entry.orderValueSort === null && orderValueSort !== null) {
    entry.orderValueSort = orderValueSort;
    entry.orderValueDisplay = formatNumberDisplay(row.orderValueInSEK);
    entry.orderValueExport = orderValueSort;
  }
}

function buildPurchaseOrderArticleRow(row, index, purchaseOrderKey) {
  const articleId = normalizeText(row.articleId);
  const articleName = normalizeText(row.articleName);

  return {
    key: `${purchaseOrderKey}-${articleId || 'article'}-${index}`,
    articleIdDisplay: articleId || '-',
    articleNameDisplay: articleName || '-',
    orderedQuantityDisplay: formatNumberDisplay(row.orderedQuantity),
    orderedQuantityExport: toExportValue(row.orderedQuantity),
    receivedQuantityDisplay: formatNumberDisplay(row.receivedQuantity ?? row.quantity),
    receivedQuantityExport: toExportValue(row.receivedQuantity ?? row.quantity),
    remainingQuantityDisplay: formatNumberDisplay(
      row.remainingQuantity ?? row.remainingOrderedQuantity
    ),
    remainingQuantityExport: toExportValue(
      row.remainingQuantity ?? row.remainingOrderedQuantity
    ),
    backOrderQuantityDisplay: formatNumberDisplay(row.backOrderQuantity),
    backOrderQuantityExport: toExportValue(row.backOrderQuantity),
    priceDisplay: formatNumberDisplay(row.price),
    priceExport: toExportValue(row.price),
  };
}

function buildPurchaseOrderRows(rawRows) {
  const groupedOrders = new Map();
  const todayStart = dayjs().startOf('day').valueOf();

  rawRows.forEach((row, index) => {
    const groupKey = getPurchaseOrderGroupingKey(row, index);
    const entry = groupedOrders.get(groupKey);

    if (!entry) {
      groupedOrders.set(groupKey, createPurchaseOrderEntry(groupKey, row));
    } else {
      mergePurchaseOrderEntry(entry, row);
    }

    groupedOrders.get(groupKey).rows.push(
      buildPurchaseOrderArticleRow(row, index, groupKey)
    );
  });

  return Array.from(groupedOrders.values()).map((entry) => ({
    ...entry,
    articleCount: entry.rows.length,
    articleCountDisplay: entry.rows.length.toLocaleString(),
    isPastDelivery:
      entry.deliveryDateSort !== null && entry.deliveryDateSort < todayStart,
  }));
}

function sortPurchaseOrders(rows, orderBy, order) {
  return [...rows].sort((a, b) => {
    const valueA = a[orderBy];
    const valueB = b[orderBy];

    if (valueA === null && valueB === null) {
      return 0;
    }

    if (valueA === null) {
      return 1;
    }

    if (valueB === null) {
      return -1;
    }

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return order === 'asc' ? valueA - valueB : valueB - valueA;
    }

    const comparison = SORT_COLLATOR.compare(String(valueA), String(valueB));
    return order === 'asc' ? comparison : -comparison;
  });
}

function getLoadErrorMessage(status) {
  if (status === 401) {
    return 'Lost connection to Fortnox. Please reconnect.';
  }

  if (status === 429) {
    return 'Fortnox cannot handle requests right now. Please wait and try again.';
  }

  return 'Failed to load purchase orders for the selected month.';
}

const PurchaseOrdersTable = memo(function PurchaseOrdersTable({
  dataVersion,
  onRequestSort,
  order,
  orderBy,
  rows,
}) {
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    setExpandedRows({});
  }, [dataVersion]);

  const toggleExpandedRow = useCallback((rowKey) => {
    setExpandedRows((currentValue) => ({
      ...currentValue,
      [rowKey]: !currentValue[rowKey],
    }));
  }, []);

  return (
    <TableContainer component={Paper} elevation={0} sx={SURFACE_SX}>
      <Table size="small" sx={{ minWidth: 980 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={TABLE_HEADER_SX} />
            <TableCell
              sortDirection={orderBy === 'purchaseOrderIdSort' ? order : false}
              sx={TABLE_HEADER_SX}
            >
              <TableSortLabel
                active={orderBy === 'purchaseOrderIdSort'}
                direction={orderBy === 'purchaseOrderIdSort' ? order : 'asc'}
                onClick={() => onRequestSort('purchaseOrderIdSort')}
              >
                PO #
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={orderBy === 'supplierNameSort' ? order : false}
              sx={{ ...TABLE_HEADER_SX, width: { xs: '32%', md: '22%' } }}
            >
              <TableSortLabel
                active={orderBy === 'supplierNameSort'}
                direction={orderBy === 'supplierNameSort' ? order : 'asc'}
                onClick={() => onRequestSort('supplierNameSort')}
              >
                Supplier
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={orderBy === 'orderDateSort' ? order : false}
              sx={TABLE_HEADER_SX}
            >
              <TableSortLabel
                active={orderBy === 'orderDateSort'}
                direction={orderBy === 'orderDateSort' ? order : 'asc'}
                onClick={() => onRequestSort('orderDateSort')}
              >
                Order Date
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={orderBy === 'statusSort' ? order : false}
              sx={TABLE_HEADER_SX}
            >
              <TableSortLabel
                active={orderBy === 'statusSort'}
                direction={orderBy === 'statusSort' ? order : 'asc'}
                onClick={() => onRequestSort('statusSort')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={orderBy === 'deliveryDateSort' ? order : false}
              sx={TABLE_HEADER_SX}
            >
              <TableSortLabel
                active={orderBy === 'deliveryDateSort'}
                direction={orderBy === 'deliveryDateSort' ? order : 'asc'}
                onClick={() => onRequestSort('deliveryDateSort')}
              >
                Delivery Date
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sortDirection={orderBy === 'orderValueSort' ? order : false}
              sx={TABLE_HEADER_SX}
            >
              <TableSortLabel
                active={orderBy === 'orderValueSort'}
                direction={orderBy === 'orderValueSort' ? order : 'asc'}
                onClick={() => onRequestSort('orderValueSort')}
              >
                Order Value (SEK)
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={TABLE_HEADER_SX}>
              # Articles
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                No purchase orders for this month.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const isExpanded = Boolean(expandedRows[row.key]);
              const expandLabelTarget =
                row.purchaseOrderIdDisplay === '-'
                  ? 'purchase order details'
                  : `purchase order ${row.purchaseOrderIdDisplay}`;

              return (
                <React.Fragment key={row.key}>
                  <TableRow
                    hover
                    sx={row.isPastDelivery ? OVERDUE_TABLE_ROW_SX : TABLE_ROW_HOVER_SX}
                  >
                    <TableCell>
                      <IconButton
                        aria-label={
                          isExpanded
                            ? `Collapse ${expandLabelTarget}`
                            : `Expand ${expandLabelTarget}`
                        }
                        color="primary"
                        onClick={() => toggleExpandedRow(row.key)}
                        size="small"
                        sx={isExpanded ? EXPAND_BUTTON_ACTIVE_SX : EXPAND_BUTTON_SX}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.purchaseOrderIdDisplay}</TableCell>
                    <TableCell sx={{ width: { xs: '32%', md: '22%' } }}>
                      {row.supplierNameDisplay}
                    </TableCell>
                    <TableCell>{row.orderDateDisplay}</TableCell>
                    <TableCell>
                      <Box component="span" sx={STATUS_PILL_SX[row.statusTone]}>
                        {row.statusDisplay}
                      </Box>
                    </TableCell>
                    <TableCell>{row.deliveryDateDisplay}</TableCell>
                    <TableCell align="right">{row.orderValueDisplay}</TableCell>
                    <TableCell align="right">{row.articleCountDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 1.5, py: 1.25 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Articles
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={TABLE_HEADER_SX}>Article ID</TableCell>
                                <TableCell sx={TABLE_HEADER_SX}>Description</TableCell>
                                <TableCell align="right" sx={TABLE_HEADER_SX}>
                                  Ordered
                                </TableCell>
                                <TableCell align="right" sx={TABLE_HEADER_SX}>
                                  Received
                                </TableCell>
                                <TableCell align="right" sx={TABLE_HEADER_SX}>
                                  Remaining
                                </TableCell>
                                <TableCell align="right" sx={TABLE_HEADER_SX}>
                                  Backorder
                                </TableCell>
                                <TableCell align="right" sx={TABLE_HEADER_SX}>
                                  Price
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.rows.map((articleRow) => (
                                <TableRow key={articleRow.key} sx={TABLE_ROW_HOVER_SX}>
                                  <TableCell>{articleRow.articleIdDisplay}</TableCell>
                                  <TableCell>{articleRow.articleNameDisplay}</TableCell>
                                  <TableCell align="right">
                                    {articleRow.orderedQuantityDisplay}
                                  </TableCell>
                                  <TableCell align="right">
                                    {articleRow.receivedQuantityDisplay}
                                  </TableCell>
                                  <TableCell align="right">
                                    {articleRow.remainingQuantityDisplay}
                                  </TableCell>
                                  <TableCell align="right">
                                    {articleRow.backOrderQuantityDisplay}
                                  </TableCell>
                                  <TableCell align="right">
                                    {articleRow.priceDisplay}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export default function PurchaseOrdersPage({
  connected,
  loadingConnection,
  setConnected,
  setError,
}) {
  const [selectedMonth, setSelectedMonth] = useState(() => clampMonth(dayjs()));
  const [rawPurchaseOrderRows, setRawPurchaseOrderRows] = useState([]);
  const [rowsMeta, setRowsMeta] = useState(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('orderDateSort');
  const [loading, setLoading] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);

  const purchaseOrders = useMemo(
    () => buildPurchaseOrderRows(rawPurchaseOrderRows),
    [rawPurchaseOrderRows]
  );

  const sortedPurchaseOrders = useMemo(
    () => sortPurchaseOrders(purchaseOrders, orderBy, order),
    [purchaseOrders, orderBy, order]
  );

  const loadPurchaseOrders = useCallback(
    async ({ allowDisconnectedMessage = false } = {}) => {
      if (!connected) {
        if (allowDisconnectedMessage) {
          setConnected(false);
          setError('Not connected to Fortnox. Please connect first.');
          setRawPurchaseOrderRows([]);
          setRowsMeta(null);
          setDataVersion((currentValue) => currentValue + 1);
        }

        return;
      }

      setLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams({
          month: selectedMonth.format('YYYY-MM'),
        });
        const response = await apiFetch(
          `/api/purchase-orders/rows?${queryParams.toString()}`
        );

        if (!response.ok) {
          const errorText = await response.text();

          console.error('Failed to load purchase orders response:', {
            errorText,
            status: response.status,
          });

          setConnected(response.status === 429);
          setError(getLoadErrorMessage(response.status));
          setRawPurchaseOrderRows([]);
          setRowsMeta(null);
          setDataVersion((currentValue) => currentValue + 1);
          return;
        }

        const payload = await response.json();
        const responseRows = Array.isArray(payload) ? payload : payload?.rows;
        const nextRows = Array.isArray(responseRows) ? responseRows : [];
        const nextMeta = Array.isArray(payload) ? null : payload?.meta || null;

        setRawPurchaseOrderRows(nextRows);
        setRowsMeta(nextMeta);
        setConnected(true);
        setDataVersion((currentValue) => currentValue + 1);
      } catch (error) {
        console.error('Failed to load purchase orders:', error);
        setConnected(false);
        setError('Failed to load purchase orders. Please try again.');
        setRawPurchaseOrderRows([]);
        setRowsMeta(null);
        setDataVersion((currentValue) => currentValue + 1);
      } finally {
        setLoading(false);
      }
    },
    [connected, selectedMonth, setConnected, setError]
  );

  useEffect(() => {
    if (loadingConnection || !connected) {
      return;
    }

    void loadPurchaseOrders();
  }, [connected, loadingConnection, loadPurchaseOrders]);

  const handleMonthChange = useCallback((event) => {
    const nextValue = event.target.value;

    if (!nextValue) {
      return;
    }

    setSelectedMonth(clampMonth(dayjs(`${nextValue}-01`)));
  }, []);

  const handleRefresh = useCallback(() => {
    void loadPurchaseOrders({ allowDisconnectedMessage: true });
  }, [loadPurchaseOrders]);

  const handleRequestSort = useCallback((property) => {
    setOrder((currentOrder) => {
      const isAscending = orderBy === property && currentOrder === 'asc';
      return isAscending ? 'desc' : 'asc';
    });
    setOrderBy(property);
  }, [orderBy]);

  const handleExport = useCallback(() => {
    if (!sortedPurchaseOrders.length) {
      setError('No purchase orders to export.');
      return;
    }

    try {
      const summaryData = sortedPurchaseOrders.map((purchaseOrder) => ({
        'PO #': purchaseOrder.purchaseOrderIdDisplay === '-' ? '' : purchaseOrder.purchaseOrderIdDisplay,
        Supplier:
          purchaseOrder.supplierNameDisplay === '-'
            ? ''
            : purchaseOrder.supplierNameDisplay,
        'Order Date':
          purchaseOrder.orderDateDisplay === '-' ? '' : purchaseOrder.orderDateDisplay,
        Status: purchaseOrder.statusDisplay,
        'Delivery Date':
          purchaseOrder.deliveryDateDisplay === '-'
            ? ''
            : purchaseOrder.deliveryDateDisplay,
        'Order Value (SEK)': purchaseOrder.orderValueExport,
        '# Articles': purchaseOrder.articleCount,
      }));

      const detailRows = sortedPurchaseOrders.flatMap((purchaseOrder) =>
        purchaseOrder.rows.map((articleRow) => [
          purchaseOrder.purchaseOrderIdDisplay === '-'
            ? ''
            : purchaseOrder.purchaseOrderIdDisplay,
          purchaseOrder.supplierNameDisplay === '-'
            ? ''
            : purchaseOrder.supplierNameDisplay,
          purchaseOrder.orderDateDisplay === '-'
            ? ''
            : purchaseOrder.orderDateDisplay,
          articleRow.articleIdDisplay === '-' ? '' : articleRow.articleIdDisplay,
          articleRow.articleNameDisplay === '-' ? '' : articleRow.articleNameDisplay,
          articleRow.orderedQuantityExport,
          articleRow.receivedQuantityExport,
          articleRow.remainingQuantityExport,
          articleRow.backOrderQuantityExport,
          articleRow.priceExport,
        ])
      );

      const workbook = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);

      summarySheet['!cols'] = [
        { wch: 10 },
        { wch: 22 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 16 },
        { wch: 10 },
        { wch: 12 },
        { wch: 14 },
        { wch: 11 },
      ];

      if (detailRows.length) {
        const summaryRowCount = summaryData.length + 1;

        XLSX.utils.sheet_add_aoa(
          summarySheet,
          [[''], ['Purchase Order Article Details']],
          {
            origin: `A${summaryRowCount + 1}`,
          }
        );

        XLSX.utils.sheet_add_aoa(
          summarySheet,
          [[
            'PO #',
            'Supplier',
            'Order Date',
            'Article ID',
            'Description',
            'Ordered',
            'Received',
            'Remaining',
            'Backorder',
            'Price',
          ]],
          {
            origin: `A${summaryRowCount + 3}`,
          }
        );

        XLSX.utils.sheet_add_aoa(summarySheet, detailRows, {
          origin: `A${summaryRowCount + 4}`,
        });
      }

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Purchase Orders');
      XLSX.writeFile(workbook, `purchase_orders_${selectedMonth.format('YYYY-MM')}.xlsx`);
    } catch (error) {
      console.error('Export purchase orders failed:', error);
      setError('Failed to export purchase orders.');
    }
  }, [selectedMonth, setError, sortedPurchaseOrders]);

  const truncatedMessage = rowsMeta?.truncated
    ? rowsMeta.message ||
      `Displaying ${rowsMeta.returnedCount ?? 'some'} purchase orders (limit ${
        rowsMeta.maxRecords ?? '?'
      }) out of ${rowsMeta.fetchedCount ?? 'many'}. Results are truncated.`
    : null;

  return (
    <ThemeProvider theme={PURCHASE_ORDERS_THEME}>
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BACKGROUND,
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'rgba(0,105,92,0.14)',
                      color: pageColors.purchaseOrders,
                    }}
                  >
                    <ShoppingCartIcon fontSize="medium" />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: pageColors.purchaseOrders, letterSpacing: 3 }}
                    >
                      PURCHASE ORDERS
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      Purchase Orders
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      Review monthly purchase orders and expand a row to inspect
                      article details.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select a month, refresh the current dataset, or export the
                    sorted result.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.25,
                    alignItems: 'start',
                  }}
                >
                  <TextField
                    label="Month"
                    type="month"
                    value={getMonthInputValue(selectedMonth)}
                    onChange={handleMonthChange}
                    size="small"
                    fullWidth
                    sx={MONTH_FIELD_SX}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: MIN_MONTH.format('YYYY-MM'),
                      max: `${Math.min(MAX_MONTH.year(), CURRENT_YEAR)}-12`,
                    }}
                  />
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={ACTION_BUTTON_STACK_SX}
                  >
                    <Button
                      color="primary"
                      disabled={loading}
                      onClick={handleRefresh}
                      sx={ACTION_BUTTON_SX}
                      variant="contained"
                    >
                      {loading ? 'Loading...' : 'Refresh'}
                    </Button>
                    <Button
                      disabled={loading || !sortedPurchaseOrders.length}
                      onClick={handleExport}
                      startIcon={<DownloadIcon />}
                      sx={ACTION_BUTTON_SX}
                      variant="outlined"
                    >
                      Export
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            {loading ? (
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            ) : null}

            {truncatedMessage ? <Alert severity="info">{truncatedMessage}</Alert> : null}

            <PurchaseOrdersTable
              dataVersion={dataVersion}
              onRequestSort={handleRequestSort}
              order={order}
              orderBy={orderBy}
              rows={sortedPurchaseOrders}
            />
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
